/**
 * Speech Recognition Utilities
 * 
 * Features:
 * - Web Speech API browser compatibility detection
 * - Speech recognition instance initialization and configuration
 * - Event handler setup for speech recognition results and errors
 * - Speech recognition state management helpers
 * 
 * Logic:
 * - Detects browser support for Speech Recognition API (standard and webkit)
 * - Creates and configures speech recognition instances with optimal settings
 * - Provides standardized event handlers for results, errors, and state changes
 * - Manages speech recognition lifecycle (start, stop, abort)
 * 
 * Changes:
 * - Extracted from Chat.tsx component for better separation of concerns
 * - Added browser compatibility detection as standalone function
 * - Created reusable speech recognition setup and configuration
 * - Standardized error handling for speech recognition events
 */

// Type declarations for Web Speech API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

export interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
}

export interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

export interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

export interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

export interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

export interface SpeechRecognitionConfig {
  continuous?: boolean;
  interimResults?: boolean;
  lang?: string;
}

export interface SpeechRecognitionHandlers {
  onResult: (transcript: string) => void;
  onError: (error: Event) => void;
  onStart?: () => void;
  onEnd?: () => void;
}

/**
 * Checks if Speech Recognition API is supported in the current browser
 * @returns True if Speech Recognition is supported
 */
export const isSpeechRecognitionSupported = (): boolean => {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

/**
 * Gets the Speech Recognition constructor for the current browser
 * @returns Speech Recognition constructor or null if not supported
 */
export const getSpeechRecognitionConstructor = (): any => {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

/**
 * Creates and configures a speech recognition instance
 * @param config Configuration options for speech recognition
 * @param handlers Event handlers for speech recognition events
 * @returns Configured speech recognition instance or null if not supported
 */
export const createSpeechRecognition = (
  config: SpeechRecognitionConfig = {},
  handlers: SpeechRecognitionHandlers
): SpeechRecognition | null => {
  const SpeechRecognition = getSpeechRecognitionConstructor();

  if (!SpeechRecognition) {
    console.warn('Speech recognition not supported in this browser.');
    return null;
  }

  const recognition = new SpeechRecognition();

  // Configure recognition settings
  recognition.continuous = config.continuous ?? false;
  recognition.interimResults = config.interimResults ?? false;
  recognition.lang = config.lang ?? 'en-US';

  // Set up event handlers
  recognition.onresult = (event: SpeechRecognitionEvent) => {
    const transcript = event.results[event.resultIndex].item(0).transcript;
    handlers.onResult(transcript);
  };

  recognition.onerror = (event: Event) => {
    console.error('Speech recognition error:', event);
    handlers.onError(event);
  };

  if (handlers.onStart) {
    recognition.onstart = handlers.onStart;
  }

  if (handlers.onEnd) {
    recognition.onend = handlers.onEnd;
  }

  return recognition;
};

/**
 * Safely starts speech recognition
 * @param recognition Speech recognition instance
 * @returns True if started successfully
 */
export const startSpeechRecognition = (recognition: SpeechRecognition | null): boolean => {
  if (!recognition) return false;

  try {
    recognition.start();
    return true;
  } catch (error) {
    console.error('Failed to start speech recognition:', error);
    return false;
  }
};

/**
 * Safely stops speech recognition
 * @param recognition Speech recognition instance
 */
export const stopSpeechRecognition = (recognition: SpeechRecognition | null): void => {
  if (!recognition) return;

  try {
    recognition.stop();
  } catch (error) {
    console.error('Failed to stop speech recognition:', error);
  }
};

/**
 * Safely aborts speech recognition
 * @param recognition Speech recognition instance
 */
export const abortSpeechRecognition = (recognition: SpeechRecognition | null): void => {
  if (!recognition) return;

  try {
    recognition.abort();
  } catch (error) {
    console.error('Failed to abort speech recognition:', error);
  }
};

/**
 * Cleans up speech recognition resources
 * @param recognition Speech recognition instance
 */
export const cleanupSpeechRecognition = (recognition: SpeechRecognition | null): void => {
  if (!recognition) return;

  stopSpeechRecognition(recognition);
  abortSpeechRecognition(recognition);
};
